"use client";

/**
 * The banner and the settings panel. Every word comes from the CMS.
 *
 * Two rules this markup exists to keep. Rejecting is a button of the same weight
 * as accepting, because a reject that is harder to find is not consent. And no
 * toggle starts on: the panel opens from what the visitor last saved, which is
 * nothing at all the first time.
 */
import { useEffect, useState } from "react";

import type { ConsentContent } from "./adapter";
import type { ConsentChoices } from "./types";
import { useConsent } from "./ConsentProvider";

export function CookieBanner({
  content,
  policyHref,
}: {
  content: ConsentContent;
  /** The policy link, already resolved by ConsentGateway. */
  policyHref: string;
}) {
  const {
    ready,
    mustAsk,
    panelOpen,
    choices,
    acceptAll,
    rejectAll,
    save,
    reopen,
  } = useConsent();
  const [draft, setDraft] = useState<ConsentChoices>(choices);

  // Opening the panel shows what is actually stored, not what was on screen
  // last time it closed.
  useEffect(() => {
    if (panelOpen || mustAsk) setDraft(choices);
  }, [panelOpen, mustAsk, choices]);

  // Nothing paints before the stored choice has been read.
  if (!ready || (!mustAsk && !panelOpen)) return null;

  const optional = content.categories.filter((c) => c.key !== "necessary");
  const showPanel = panelOpen;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={content.bannerTitle}
      data-testid="cookie-banner"
      className="inset-x-0 bottom-0 p-4 shadow-lg sm:p-6 fixed z-50 max-h-[85dvh] overflow-y-auto overscroll-contain border-t border-border bg-background"
    >
      <div className="max-w-5xl gap-4 mx-auto flex flex-col">
        <div>
          <h2 className="font-bold text-base">{content.bannerTitle}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {content.bannerBody}{" "}
            <a href={policyHref} className="underline">
              {content.policyLinkText}
            </a>
          </p>
        </div>

        {showPanel && (
          <div className="gap-3 pt-3 flex flex-col border-t border-border">
            <h3 className="font-bold text-sm">{content.settingsTitle}</h3>
            {content.categories.map((category) => {
              const locked = category.key === "necessary";
              const key = category.key as keyof ConsentChoices;
              const inputId = `consent-${category.key}`;
              const descriptionId = `${inputId}-description`;
              return (
                <div key={category.key} className="gap-3 flex items-start">
                  {locked ? (
                    <span
                      className="mt-0.5 font-semibold tracking-wide shrink-0 text-xs text-muted-foreground uppercase"
                      data-testid={`consent-locked-${category.key}`}
                    >
                      {content.alwaysOnLabel}
                    </span>
                  ) : (
                    <input
                      type="checkbox"
                      className="mt-0.5 size-5 shrink-0 cursor-pointer"
                      id={inputId}
                      data-testid={`consent-toggle-${category.key}`}
                      aria-describedby={descriptionId}
                      checked={draft[key] === true}
                      onChange={(event) =>
                        setDraft({ ...draft, [key]: event.target.checked })
                      }
                    />
                  )}
                  {/* The name is the label; the explanation is described-by, not
                      part of it. Nesting elements inside a label also trips a
                      latent eslint crash in this repo (#969). */}
                  <div className="text-sm">
                    {locked ? (
                      <span className="font-semibold">{category.label}</span>
                    ) : (
                      <label
                        htmlFor={inputId}
                        className="font-semibold cursor-pointer"
                      >
                        {category.label}
                      </label>
                    )}
                    <p id={descriptionId} className="text-muted-foreground">
                      {category.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Sticky inside the banner's own scroll area: with the choices open on a
            phone the list is taller than the banner, and buttons that scroll away
            leave the visitor unable to answer without discovering the scroll. */}
        <div className="gap-2 bottom-0 pt-2 pb-1 sticky flex flex-wrap bg-background">
          <button
            type="button"
            onClick={acceptAll}
            data-testid="consent-accept-all"
            className="px-4 py-2.5 font-bold cursor-pointer rounded-full bg-primary text-sm text-primary-foreground"
          >
            {content.acceptAllLabel}
          </button>
          {/* Same size, same prominence, right next to accept. A reject hidden
              behind a second click is not a free choice. */}
          <button
            type="button"
            onClick={rejectAll}
            data-testid="consent-reject-all"
            className="px-4 py-2.5 font-bold cursor-pointer rounded-full border border-border text-sm"
          >
            {content.rejectAllLabel}
          </button>
          {showPanel ? (
            <button
              type="button"
              onClick={() => save(draft)}
              data-testid="consent-save"
              className="px-4 py-2.5 font-bold cursor-pointer rounded-full border border-border text-sm"
            >
              {content.savePreferencesLabel}
            </button>
          ) : (
            optional.length > 0 && (
              <button
                type="button"
                // Opening the choices is not a decision, so nothing is stored
                // here — only the panel is revealed.
                onClick={reopen}
                data-testid="consent-manage"
                className="px-4 py-2.5 font-bold cursor-pointer rounded-full text-sm underline"
              >
                {content.managePreferencesLabel}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
